import type { ReactNode } from "react";
import clsx from "clsx";
import { fmtDateTime, timeAgo } from "../format";
import { Expandable, JsonView } from "./JsonView";
import { useT } from "../i18n";

/** Mono value (ids, keys, expressions). */
export function Mono({ children, className }: { children: ReactNode; className?: string }) {
  return <code className={clsx("text-mono", className)}>{children}</code>;
}

/** Empty value "–" (HANDOFF §7). */
export function Dash() { return <span className="text-fg-3">–</span>; }
export function orDash(v: ReactNode | null | undefined): ReactNode { return v === null || v === undefined || v === "" ? <Dash /> : v; }

/** Date/time per §7 ("Fri 10 Oct · 09:14"); `relative` shows "3 hours ago" with the full stamp as title. */
export function When({ value, relative }: { value?: string | null; relative?: boolean }) {
  if (!value) return <Dash />;
  return <span title={fmtDateTime(value)}>{relative ? timeAgo(value) : fmtDateTime(value)}</span>;
}

/** "Details" expander with a JSON view; renders nothing for empty values. */
export function Details({ value, summary }: { value: unknown; summary?: string }) {
  const t = useT();
  if (value === null || value === undefined || (typeof value === "object" && Object.keys(value as object).length === 0)) return null;
  return <Expandable summary={summary ?? t("ui.details")}><JsonView value={value} maxHeight="16rem" /></Expandable>;
}

/** Section title row (h3 inside tabs and panels; `heading="h2"` for portal page sections) + content. */
export function Section({ title, children, actions, className, heading = "h3" }: { title?: ReactNode; children: ReactNode; actions?: ReactNode; className?: string; heading?: "h2" | "h3" }) {
  return (
    <section className={clsx("flex flex-col", heading === "h2" ? "gap-3" : "gap-2", className)}>
      {(title || actions) && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          {title && (heading === "h2" ? <h2 className="text-h2">{title}</h2> : <h3 className="text-h3">{title}</h3>)}
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}
